import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';
import { parseReport } from '../utils/reportParser';
import { detectTerms } from '../utils/termDetector';
import { getKnowledge } from '../api';
import KnowledgePopup from './KnowledgePopup';

import HeaderCard    from './dashboard/HeaderCard';
import ProductsCard  from './dashboard/ProductsCard';
import TokenomicsCard from './dashboard/TokenomicsCard';
import MetricsCard   from './dashboard/MetricsCard';
import TeamCard      from './dashboard/TeamCard';
import EventsUnlockCard from './dashboard/EventsUnlockCard';
import RoadmapCard   from './dashboard/RoadmapCard';
import PotentialCard from './dashboard/PotentialCard';
import { Card } from './dashboard/shared';
import MarketAlertBanner from './MarketAlertBanner';

// ── Inline term highlight (shared across all text in dashboard) ────
function T({ children, onTermHover, onTermLeave }) {
  const text = String(children);
  const segments = detectTerms(text);
  if (segments.length === 1 && !segments[0].canonical) return <>{text}</>;

  return (
    <>
      {segments.map((seg, i) =>
        seg.canonical ? (
          <span
            key={i}
            style={termStyle}
            onMouseEnter={e => onTermHover(seg.canonical, e.clientX, e.clientY)}
            onMouseMove={e => onTermHover(seg.canonical, e.clientX, e.clientY)}
            onMouseLeave={onTermLeave}
          >
            {seg.text}
          </span>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        )
      )}
    </>
  );
}

const termStyle = {
  color: '#b45309',
  borderBottom: '1px dashed #d9770680',
  cursor: 'default',
  borderRadius: 2,
  padding: '0 1px',
};

// ── TL;DR card ─────────────────────────────────────────────────────
function TldrCard({ text, onTermHover, onTermLeave }) {
  const { t } = useTranslation();
  return (
    <Card title={t('reportDashboard.tldrTitle')} icon="💡" accent="#06b6d4">
      <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.75, margin: 0 }}>
        <T onTermHover={onTermHover} onTermLeave={onTermLeave}>{text}</T>
      </p>
    </Card>
  );
}

// ── Applications card ──────────────────────────────────────────────
function ApplicationsCard({ data, onTermHover, onTermLeave }) {
  const { t } = useTranslation();
  return (
    <Card title={t('reportDashboard.applicationsTitle')} icon="🌐">
      {data.useCases.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={styles.subLabel}>{t('reportDashboard.liveUseCases')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.useCases.map((u, i) => (
              <div key={i} style={styles.useCase}>
                <span style={styles.useCaseDot}>▸</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}><T onTermHover={onTermHover} onTermLeave={onTermLeave}>{u}</T></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.partnerships.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={styles.subLabel}>{t('reportDashboard.activePartnerships')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.partnerships.map((p, i) => (
              <div key={i} style={styles.partnership}>
                <span style={styles.partnerName}>{p.name}</span>
                <span style={styles.partnerDesc}><T onTermHover={onTermHover} onTermLeave={onTermLeave}>{p.desc}</T></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.values(data.metrics).some(Boolean) && (
        <div>
          <div style={styles.subLabel}>{t('reportDashboard.onchainMetrics')}</div>
          <div style={styles.metricsGrid}>
            {[
              { label: t('reportDashboard.metricTvl'), icon: '🔒', value: data.metrics.tvl },
              { label: t('reportDashboard.metricDailyUsers'), icon: '👤', value: data.metrics.daily_active },
              { label: t('reportDashboard.metricTransactions'), icon: '⚡', value: data.metrics.total_transactions },
              { label: t('reportDashboard.metricWallets'), icon: '👛', value: data.metrics.unique_wallets },
            ].filter(m => m.value).map(m => (
              <div key={m.label} style={styles.metricChip}>
                <span>{m.icon}</span>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 600 }}>{m.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 700 }}>{m.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function ReportDashboard({ doc, onBack, onRefresh, onPersonClick, marketAlerts }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const parsed = parseReport(doc.report);
  const [popup, setPopup] = useState(null); // { canonical, mouseX, mouseY, data, loading, error }
  const cacheRef = useRef({}); // term → fetched data, avoid re-fetching on mousemove

  const handleTermHover = useCallback(async (canonical, mouseX, mouseY) => {
    // Update position every mousemove even if already showing same term
    setPopup(prev => {
      if (prev?.canonical === canonical) {
        return { ...prev, mouseX, mouseY };
      }
      return { canonical, mouseX, mouseY, data: cacheRef.current[canonical] || null, loading: !cacheRef.current[canonical], error: null };
    });

    // Only fetch if not already cached
    if (cacheRef.current[canonical]) return;

    try {
      const data = await getKnowledge(canonical);
      cacheRef.current[canonical] = data;
      setPopup(prev => prev?.canonical === canonical ? { ...prev, data, loading: false } : prev);
    } catch {
      setPopup(prev => prev?.canonical === canonical
        ? { ...prev, loading: false, error: t('reportDashboard.termLoadError') } : prev);
    }
  }, [t]);

  const handleTermLeave = useCallback(() => setPopup(null), []);

  if (!parsed) return (
    <div style={{ color: '#64748b', padding: 40, textAlign: 'center' }}>
      {t('reportDashboard.parseError')}
    </div>
  );

  const { meta, tldr, overview, products, tokenomics, applications, achievements, team, events, potential } = parsed;

  return (
    <div style={styles.container}>
      {/* Header */}
      <HeaderCard
        overview={overview}
        meta={meta}
        verdict={potential}
        tokenomics={tokenomics}
        doc={doc}
        onBack={onBack}
        onRefresh={onRefresh}
      />

      {/* Volume-anomaly alert for this project (if any) */}
      <MarketAlertBanner alerts={marketAlerts} slug={doc.slug} />

      {/* TL;DR — full width */}
      <TldrCard text={tldr} onTermHover={handleTermHover} onTermLeave={handleTermLeave} />

      {/* Two independent masonry columns — no row-alignment gaps */}
      <div style={isMobile ? { ...styles.masonry, gridTemplateColumns: '1fr' } : styles.masonry}>
        {/* Left column */}
        <div style={styles.col}>
          <ProductsCard data={products} overview={overview} />
          <ApplicationsCard data={applications} onTermHover={handleTermHover} onTermLeave={handleTermLeave} />
          <TeamCard data={team} onPersonClick={onPersonClick}
            projectInfo={{ project: overview.name || doc.name, projectSlug: doc.slug }} />
        </div>

        {/* Right column */}
        <div style={styles.col}>
          <TokenomicsCard data={tokenomics} />
          <MetricsCard data={achievements} applications={applications} />
          <EventsUnlockCard events={events} />
          <RoadmapCard data={events.roadmap} />
          <PotentialCard data={potential} />
        </div>
      </div>

      {/* Knowledge popup — fixed at mouse position, no pointer-events */}
      {popup && (
        <KnowledgePopup
          data={popup.data}
          loading={popup.loading}
          error={popup.error}
          mouseX={popup.mouseX}
          mouseY={popup.mouseY}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    animation: 'fadeIn 0.3s ease',
    position: 'relative',
    maxWidth: '100%',
    width: '100%',
    minWidth: 0,
  },
  masonry: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
    alignItems: 'start',
    minWidth: 0,
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
    overflow: 'hidden',
  },
  subLabel: {
    fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8,
  },
  useCase: { display: 'flex', gap: 8, alignItems: 'flex-start' },
  useCaseDot: { color: 'var(--accent)', fontSize: 14, lineHeight: 1.4, flexShrink: 0 },
  partnership: {
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '8px 12px',
    display: 'flex', flexDirection: 'column', gap: 3,
  },
  partnerName: { fontSize: 12, fontWeight: 700, color: 'var(--text)' },
  partnerDesc: { fontSize: 11, color: 'var(--text-muted)' },
  metricsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  metricChip: {
    display: 'flex', gap: 10, alignItems: 'center',
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '9px 12px', fontSize: 16,
  },
};
